import { format, isValid } from 'date-fns'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox, Stack, TableContainer, TextField, Typography } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { DatePicker } from '../../../../components/DatePicker/DatePicker'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { packageTierLabelKeys, packageTypeLabelKeys } from '../../../plans/components/helpers/planHelpers'
import { InternalPackage } from '../../../plans/types/Plans'
import { AddedOrganizationPackage } from '../../types/Organizations'
import { atTime, isTime, toAddedPackages } from '../helpers/organizationHelpers'

type CustomProps = {
  selectedIds: string[]
  addedIds: string[]
  onToggle: (packageId: string) => void
}

type AddPackageDialogProps = {
  open: boolean
  organizationName: string
  catalog: InternalPackage[]
  /** Catalog ids the organization already has; they are shown checked and can't be added again. */
  addedIds: string[]
  onClose: () => void
  onAdd: (packages: AddedOrganizationPackage[]) => void
}

const currentTime = () => format(Date.now(), 'HH:mm')

export const AddPackageDialog: FC<AddPackageDialogProps> = ({ open, organizationName, catalog, addedIds, onClose, onAdd }) => {
  const { t } = useTranslation()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [purchasedDate, setPurchasedDate] = useState<Date | null>(null)
  const [purchasedTime, setPurchasedTime] = useState('')
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [expirationTime, setExpirationTime] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!open) return
    setSelectedIds([])
    setPurchasedDate(null)
    setPurchasedTime(currentTime())
    setExpirationDate(null)
    setExpirationTime(currentTime())
    setNote('')
  }, [open])

  const tableColumns: GRTableColumn<InternalPackage, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => {
          const added = !!customTableProps?.addedIds.includes(row.id)
          return (
            <Checkbox
              checked={added || !!customTableProps?.selectedIds.includes(row.id)}
              disabled={added}
              onChange={() => customTableProps?.onToggle(row.id)}
              inputProps={{ 'aria-label': row.name }}
            />
          )
        },
        headerCellProps: { sx: { width: 60, minWidth: 60 } },
      },
      {
        labelAccessor: t('internal-csm:plans_package_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.name}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-csm:plans_type'),
        accessor: ({ row }) => t(packageTypeLabelKeys[row.type] ?? '–'),
        sortable: true,
        sortAccessor: ({ row }) => row.type,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 130 } },
      },
      {
        labelAccessor: t('internal-csm:plans_tier'),
        accessor: ({ row }) => t(packageTierLabelKeys[row.tier] ?? '–'),
        sortable: true,
        sortAccessor: ({ row }) => row.tier,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 110 } },
      },
      {
        labelAccessor: t('internal-csm:plans_monthly_price'),
        accessor: ({ row }) => `${row.price} €`,
        sortable: true,
        sortAccessor: ({ row }) => row.price,
        cellProps: { align: 'right', sx: { whiteSpace: 'nowrap' } },
        headerCellProps: { sx: { minWidth: 110, whiteSpace: 'nowrap' } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      selectedIds,
      addedIds,
      onToggle: (packageId) => setSelectedIds((current) => (current.includes(packageId) ? current.filter((id) => id !== packageId) : [...current, packageId])),
    }),
    [selectedIds, addedIds]
  )

  const hasPurchasedDate = !!purchasedDate && isValid(purchasedDate)
  const hasExpirationDate = !!expirationDate && isValid(expirationDate)
  const purchasedTimeError = hasPurchasedDate && !isTime(purchasedTime)
  const expirationTimeError = hasExpirationDate && !isTime(expirationTime)
  const expirationBeforePurchase =
    hasPurchasedDate &&
    hasExpirationDate &&
    !purchasedTimeError &&
    !expirationTimeError &&
    atTime(expirationDate, expirationTime) <= atTime(purchasedDate, purchasedTime)
  const invalidExpirationDate = !!expirationDate && !hasExpirationDate
  const canAdd =
    selectedIds.length > 0 && hasPurchasedDate && !purchasedTimeError && !expirationTimeError && !invalidExpirationDate && !expirationBeforePurchase

  const add = () => {
    if (!canAdd || !purchasedDate) return
    onAdd(
      toAddedPackages(catalog, selectedIds, {
        purchasedDate,
        purchasedTime,
        expirationDate: hasExpirationDate ? expirationDate : null,
        expirationTime,
        note,
      })
    )
  }

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-csm:organizations_add_packages_title', { name: organizationName })}
      onClose={onClose}
      onSave={add}
      saveDisabled={!canAdd}
      saveLabel={t('internal-csm:organizations_add')}
      maxWidth="md"
    >
      <Stack spacing={2} pt={1}>
        <TableContainer>
          <GRTable
            rows={catalog}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-csm:organizations_no_packages_to_add')}
            striped
          />
        </TableContainer>
        <Stack direction="row" spacing={2}>
          <DatePicker
            label={t('internal-csm:organizations_purchased_date')}
            value={purchasedDate}
            onChange={setPurchasedDate}
            slotProps={{ textField: { required: true } }}
          />
          <TextField
            type="time"
            size="small"
            fullWidth
            label={t('internal-csm:organizations_purchased_time')}
            value={purchasedTime}
            onChange={(event) => setPurchasedTime(event.target.value)}
            disabled={!hasPurchasedDate}
            error={purchasedTimeError}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <DatePicker
            label={t('internal-csm:organizations_expiration_date')}
            value={expirationDate}
            onChange={setExpirationDate}
            slotProps={{
              textField: {
                error: expirationBeforePurchase || invalidExpirationDate,
                helperText: expirationBeforePurchase
                  ? t('internal-csm:organizations_expiration_before_purchase')
                  : t('internal-csm:organizations_expiration_help'),
              },
            }}
          />
          <TextField
            type="time"
            size="small"
            fullWidth
            label={t('internal-csm:organizations_expiration_time')}
            value={expirationTime}
            onChange={(event) => setExpirationTime(event.target.value)}
            disabled={!hasExpirationDate}
            error={expirationTimeError}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
        <TextField multiline minRows={3} label={t('internal-csm:organizations_sales_note')} value={note} onChange={(event) => setNote(event.target.value)} />
      </Stack>
    </InternalFormDialog>
  )
}
